import React, { useState, useEffect } from 'react';
import { Send, Check, Loader } from 'lucide-react';
import axios from 'axios';
import { trackGoal, trackEvent, GOALS } from '../../utils/yandexMetrika';
import './ContactForm.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '+7',
    agreed: false
  });
  
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [error, setError] = useState('');
  const [formStarted, setFormStarted] = useState(false);

  // Отслеживаем просмотр формы
  useEffect(() => {
    trackGoal(GOALS.FORM_OPEN);
    trackEvent('Form', 'View', 'Contact Form');
  }, []);

  // Форматирование номера телефона
  const formatPhoneNumber = (value) => {
    // Удаляем все символы кроме цифр
    let phoneNumber = value.replace(/\D/g, '');
    
    // Если номер пустой, возвращаем +7
    if (phoneNumber.length === 0) return '+7';
    
    // Если номер не начинается с 7, добавляем 7 в начало
    if (!phoneNumber.startsWith('7')) {
      phoneNumber = '7' + phoneNumber;
    }
    
    // Ограничиваем длину номера (11 цифр максимум для российского номера)
    phoneNumber = phoneNumber.slice(0, 11);
    
    const phoneNumberLength = phoneNumber.length;
    
    // Форматируем номер
    let formattedNumber = `+${phoneNumber.slice(0, 1)}`;
    
    // +7 (XXX)
    if (phoneNumberLength > 1) {
      formattedNumber += ` (${phoneNumber.slice(1, 4)}`;
    }
    
    // +7 (XXX) XXX
    if (phoneNumberLength >= 5) {
      formattedNumber += `) ${phoneNumber.slice(4, 7)}`;
    }
    
    // +7 (XXX) XXX-XX
    if (phoneNumberLength >= 8) {
      formattedNumber += `-${phoneNumber.slice(7, 9)}`;
    }
    
    // +7 (XXX) XXX-XX-XX
    if (phoneNumberLength >= 10) {
      formattedNumber += `-${phoneNumber.slice(9, 11)}`;
    }
    
    return formattedNumber;
  };

  // Форматирование имени (только буквы, первая буква каждого слова с большой)
  const formatName = (value) => {
    // Удаляем все символы кроме букв (русских и английских) и пробелов
    let name = value.replace(/[^а-яА-Яa-zA-Z\s]/g, '');
    
    // Капитализируем первую букву каждого слова
    name = name.replace(/\b\w/g, (char) => char.toUpperCase());
    name = name.replace(/\b[а-я]/g, (char) => char.toUpperCase());
    
    return name;
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formattedPhone
    }));
    
    // Отслеживаем начало заполнения формы
    if (!formStarted && formattedPhone.length > 3) {
      setFormStarted(true);
      trackGoal(GOALS.FORM_FILL_START);
      trackEvent('Form', 'Start Filling', 'Contact Form');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'phone') {
      handlePhoneChange(e);
    } else if (name === 'name') {
      const formattedName = formatName(value);
      setFormData(prev => ({
        ...prev,
        name: formattedName
      }));
      
      // Отслеживаем начало заполнения формы
      if (!formStarted && formattedName.length > 0) {
        setFormStarted(true);
        trackGoal(GOALS.FORM_FILL_START);
        trackEvent('Form', 'Start Filling', 'Contact Form');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      setError('Пожалуйста, заполните все поля');
      trackEvent('Form', 'Validation Error', 'Empty Fields');
      return;
    }

    if (!formData.agreed) {
      setError('Необходимо согласие на обработку персональных данных');
      trackEvent('Form', 'Validation Error', 'No Agreement');
      return;
    }

    // Проверяем длину номера
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 11) {
      setError('Пожалуйста, введите корректный номер телефона');
      trackEvent('Form', 'Validation Error', 'Invalid Phone');
      return;
    }

    setStatus('loading');
    setError('');

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/submit-form`, {
        name: formData.name,
        phone: formData.phone
      });
      
      if (response.data.success) {
        setStatus('success');
        setFormData({ name: '', phone: '+7', agreed: false });
        setFormStarted(false);
        
        // Отслеживаем успешную отправку
        trackGoal(GOALS.FORM_SUBMIT, {
          form_name: 'contact_form',
          form_location: window.location.pathname
        });
        trackEvent('Form', 'Submit Success', 'Contact Form');
        
        // Также отправляем событие для электронной коммерции (если понадобится в будущем)
        trackEvent('Lead', 'Generated', 'Contact Form', 1);
        
        // Сброс статуса через 3 секунды
        setTimeout(() => {
          setStatus('idle');
        }, 3000);
      }
    } catch (err) {
      setStatus('error');
      setError('Произошла ошибка при отправке формы');
      console.error(err);
      
      // Отслеживаем ошибку
      trackEvent('Form', 'Submit Error', err.message || 'Unknown Error');
    }
  };

  return (
    <section id="form" className="contact-form-section">
      <div className="container">
        <div className="form-container">
          <div className="form-header">
            <h2>Остались вопросы? Оставьте заявку!</h2>
            <p>Заполните форму ниже, и наш специалист свяжется с вами в ближайшее рабочее время для бесплатной консультации.</p>
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name">Ваше имя</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Иван Иванов"
                className="form-input"
                disabled={status === 'loading'}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Номер телефона</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+7 (999) 999-99-99"
                className="form-input"
                disabled={status === 'loading'}
                maxLength={18}
              />
            </div>

            <div className="form-checkbox">
              <input
                type="checkbox"
                id="agreed"
                name="agreed"
                checked={formData.agreed}
                onChange={handleChange}
                disabled={status === 'loading'}
              />
              <label htmlFor="agreed">
                Я даю согласие на обработку персональных данных и соглашаюсь с{' '}
                <a 
                  href="/privacy" 
                  target="_blank"
                  onClick={() => trackEvent('Link', 'Click', 'Privacy Policy')}
                >
                  политикой конфиденциальности
                </a>
              </label>
            </div>

            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className={`form-submit ${status}`}
              disabled={status === 'loading' || status === 'success'}
            >
              {status === 'idle' && (
                <>
                  Отправить заявку
                  <Send size={20} />
                </>
              )}
              {status === 'loading' && (
                <>
                  Отправляем...
                  <Loader size={20} className="spin" />
                </>
              )}
              {status === 'success' && (
                <>
                  Заявка отправлена!
                  <Check size={20} />
                </>
              )}
            </button>
          </form>

          <div className="form-footer">
            <p>Или позвоните нам напрямую:</p>
            <a 
              href="tel:+79257166607" 
              className="phone-big"
              onClick={() => {
                trackGoal(GOALS.PHONE_CLICK, { phone_location: 'form_footer' });
                trackEvent('Contact', 'Phone Click', 'Form Footer');
              }}
            >
              +7 (925) 716-66-07
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;